import { lazy, Suspense, useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import Index from "./pages/Index";
import AIChatWidget from "./components/AIChatWidget";
import AnalyticsLoader from "./components/AnalyticsLoader";
import CookieConsent from "./components/CookieConsent";
import PageScrollIndicator from "./components/PageScrollIndicator";
import { useAnalytics } from "./hooks/useAnalytics";
import { readConsent, hasGlobalPrivacyControl, CONSENT_EVENT } from "./lib/consent";
import WebDevLanding from "./pages/WebDevLanding";
import { isLandingPath } from "./lib/landingPaths";
import { captureAttribution } from "./lib/attribution";

const Consultation = lazy(() => import("./pages/Consultation"));
const About = lazy(() => import("./pages/About"));
const Industries = lazy(() => import("./pages/Industries"));
const ServicePage = lazy(() => import("./pages/ServicePage"));
const Contact = lazy(() => import("./pages/Contact"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const Careers = lazy(() => import("./pages/Careers"));
const OurWork = lazy(() => import("./pages/OurWork"));
const NotFound = lazy(() => import("./pages/NotFound"));
const YourPrivacyChoices = lazy(() => import("./pages/YourPrivacyChoices"));
const AdminApp = lazy(() => import("./admin/AdminApp"));

const queryClient = new QueryClient();

function ChatWidgetWrapper() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/112076");
  const [hasDecision, setHasDecision] = useState(
    () => typeof window !== "undefined" && (!!readConsent() || hasGlobalPrivacyControl())
  );
  useEffect(() => {
    if (hasDecision) return;
    const onChange = () => setHasDecision(true);
    window.addEventListener(CONSENT_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_EVENT, onChange);
  }, [hasDecision]);
  if (isAdmin || isLandingPath(location.pathname)) return null;
  return <AIChatWidget lifted={!hasDecision} />;
}

function PageScrollIndicatorWrapper() {
  const { pathname } = useLocation();
  // Only the main site has the fixed 64px header; landing and admin pages start at the top.
  const offset = pathname.startsWith("/112076") || isLandingPath(pathname) ? 0 : 64;
  return <PageScrollIndicator topOffset={offset} />;
}

function AnalyticsTracker() {
  const location = useLocation();
  useAnalytics();
  useEffect(() => { captureAttribution(); }, [location.pathname, location.search]);
  return null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <AnalyticsLoader />
        <AnalyticsTracker />
        <Suspense fallback={<div className="min-h-screen bg-background" />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/about-us" element={<About />} />
            <Route path="/about" element={<Navigate to="/about-us" replace />} />
            <Route path="/industries" element={<Industries />} />
            <Route path="/consultation" element={<Consultation />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfUse />} />
            <Route path="/terms-of-use" element={<Navigate to="/terms-of-service" replace />} />
            <Route path="/your-privacy-choices" element={<YourPrivacyChoices />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/our-work" element={<OurWork />} />
            <Route path="/services/:slug" element={<ServicePage />} />
            <Route path="/web-development-san-diego" element={<WebDevLanding />} />
            <Route path="/112076/*" element={<AdminApp />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <ChatWidgetWrapper />
        <CookieConsent />
        <PageScrollIndicatorWrapper />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
